import { type ReactNode, useMemo } from "react";
import { useClient } from "#/hook/useClient";
import type { PrvPattern, PrvPatternsView } from "#/types/prv";
import { formatDate } from "#/utils/dateTimeUtils";
import type { PrvSelection } from "./PrvHeatmap";
import { withBoldValues } from "./prvText";
import { averageStaySeconds, checkTarget, formatDuration, intensityPer30Days } from "./prvUtils";
import { usePrvTranslation } from "./usePrvTranslation";

interface Props {
  view: PrvPatternsView;
  /** Patterns inside the current filter selection. */
  scoped: PrvPattern[];
  selection: PrvSelection;
}

const STALE_DAYS = 30;
const MAX_NAMES = 3;

/**
 * Plain-language digest of the patterns in the current selection. Everything
 * here is derived from the same numbers the charts show. Rotation targets are
 * checked against their latest interval only.
 */
const PrvSummaryBox = ({ view, scoped, selection }: Props) => {
  const t = usePrvTranslation();
  const { client } = useClient();

  const nameOfSub = (id: number) => view.subClients.find((s) => s.id === id)?.name ?? `#${id}`;
  const nameOfCategory = (id: number) => view.categories.find((c) => c.id === id)?.name ?? `#${id}`;
  const joinNames = (names: string[]) => {
    const shown = names.slice(0, MAX_NAMES);
    const rest = names.length - shown.length;
    return rest > 0
      ? t("clients.prv.results.summaryBox.andMore", {
          names: shown.join(", "),
          count: rest,
        })
      : shown.join(", ");
  };

  const bullets = useMemo((): ReactNode[] => {
    const lines: ReactNode[] = [];
    const withStays = scoped.filter((p) => p.stays > 0);
    const totalStays = scoped.reduce((sum, p) => sum + p.stays, 0);
    const present = scoped.reduce((sum, p) => sum + p.present, 0);

    if (scoped.length === 0) {
      return [t("clients.prv.results.summaryBox.nothing")];
    }

    const firstDates = withStays
      .map((p) => p.firstMovementAt)
      .filter((d): d is string => d !== null)
      .sort();
    const lastDates = withStays
      .map((p) => p.lastMovementAt)
      .filter((d): d is string => d !== null)
      .sort();
    const since = firstDates[0] ? formatDate(firstDates[0], client) : null;
    const singlePair = selection.subClientId !== null && selection.productId !== null;
    const singleSub = selection.subClientId !== null && selection.productId === null;

    // 1. Overview
    if (since) {
      lines.push(
        singlePair || singleSub || selection.productId !== null
          ? withBoldValues(t, "clients.prv.results.summaryBox.overviewScoped", {
              since,
              stays: totalStays,
              present,
            })
          : withBoldValues(t, "clients.prv.results.summaryBox.overview", {
              since,
              stays: totalStays,
              subClients: new Set(withStays.map((p) => p.subClientId)).size,
              categories: new Set(withStays.map((p) => p.productId)).size,
              present,
            })
      );
    } else {
      lines.push(withBoldValues(t, "clients.prv.results.summaryBox.onlyPresent", { present }));
    }

    // 2. Busiest flow / 3. Longest stays (only meaningful with more than one pair)
    if (withStays.length > 1) {
      const busiest = [...withStays].sort(
        (a, b) => intensityPer30Days(b) - intensityPer30Days(a)
      )[0];
      lines.push(
        singleSub
          ? withBoldValues(t, "clients.prv.results.summaryBox.busiestCategory", {
              category: nameOfCategory(busiest.productId),
              perMonth: intensityPer30Days(busiest).toFixed(1),
              stay: formatDuration(averageStaySeconds(busiest), t),
            })
          : withBoldValues(t, "clients.prv.results.summaryBox.busiest", {
              category: nameOfCategory(busiest.productId),
              subClient: nameOfSub(busiest.subClientId),
              perMonth: intensityPer30Days(busiest).toFixed(1),
              stay: formatDuration(averageStaySeconds(busiest), t),
            })
      );
      const longest = [...withStays].sort(
        (a, b) => (averageStaySeconds(b) ?? 0) - (averageStaySeconds(a) ?? 0)
      )[0];
      if (longest !== busiest) {
        lines.push(
          withBoldValues(t, "clients.prv.results.summaryBox.longest", {
            category: nameOfCategory(longest.productId),
            subClient: nameOfSub(longest.subClientId),
            stay: formatDuration(averageStaySeconds(longest), t),
          })
        );
      }
    } else if (withStays.length === 1) {
      const only = withStays[0];
      lines.push(
        withBoldValues(t, "clients.prv.results.summaryBox.single", {
          perMonth: intensityPer30Days(only).toFixed(1),
          stay: formatDuration(averageStaySeconds(only), t),
          last: formatDate(only.lastMovementAt, client),
        })
      );
    }

    // 4. Slowing down: customers whose last movement is well behind the latest one seen.
    if (!singlePair && lastDates.length > 0) {
      const latest = new Date(lastDates[lastDates.length - 1]).getTime();
      const bySub = new Map<number, number>();
      for (const p of withStays) {
        if (!p.lastMovementAt) continue;
        const at = new Date(p.lastMovementAt).getTime();
        bySub.set(p.subClientId, Math.max(bySub.get(p.subClientId) ?? 0, at));
      }
      const stale = [...bySub.entries()]
        .filter(([, at]) => latest - at > STALE_DAYS * 86_400_000)
        .sort((a, b) => a[1] - b[1]);
      for (const [subId, at] of stale.slice(0, MAX_NAMES)) {
        lines.push(
          withBoldValues(t, "clients.prv.results.summaryBox.slowing", {
            subClient: nameOfSub(subId),
            date: formatDate(new Date(at).toISOString(), client),
          })
        );
      }
    }

    // 5. Never observed — a target-only zero row does not count as observed.
    const observed = scoped.filter((p) => p.stays > 0 || p.present > 0);
    if (selection.subClientId === null) {
      const seen = new Set(observed.map((p) => p.subClientId));
      const never = view.subClients.filter((s) => !seen.has(s.id)).map((s) => s.name);
      if (never.length > 0) {
        lines.push(
          withBoldValues(t, "clients.prv.results.summaryBox.neverObserved", {
            names: joinNames(never),
          })
        );
      }
    } else if (singleSub) {
      const seen = new Set(observed.map((p) => p.productId));
      const missing = view.categories.filter((c) => !seen.has(c.id)).length;
      if (missing > 0) {
        lines.push(
          withBoldValues(t, "clients.prv.results.summaryBox.neverObservedCategories", {
            count: missing,
          })
        );
      }
    }

    // 6. Product rotation targets, latest interval (last N days ending at the last run).
    const asOf = view.lastRun?.startedAt ?? view.generatedAt;
    if (singlePair) {
      const check = checkTarget(scoped[0], view.days, asOf);
      lines.push(
        check
          ? withBoldValues(
              t,
              check.status === "met"
                ? "clients.prv.results.summaryBox.targetPairMet"
                : check.status === "below"
                  ? "clients.prv.results.summaryBox.targetPairBelow"
                  : "clients.prv.results.summaryBox.targetPairOverflow",
              {
                count: check.count,
                days: check.days,
                arrived: check.arrived,
                short: check.count - check.arrived,
                over: check.arrived - check.count,
              }
            )
          : t("clients.prv.results.summaryBox.noTargetPair")
      );
    } else {
      const checks = scoped.map((p) => checkTarget(p, view.days, asOf));
      const met = checks.filter((c) => c?.status === "met").length;
      const below = checks.filter((c) => c?.status === "below").length;
      const overflow = checks.filter((c) => c?.status === "overflow").length;
      const none = checks.filter((c) => c === null).length;
      lines.push(
        <>
          {t("clients.prv.results.summaryBox.targetsTitle")}
          <ul className="mt-1 list-[circle] space-y-0.5 pl-4">
            <li>
              {withBoldValues(t, "clients.prv.results.summaryBox.targetsMet", { count: met })}
            </li>
            <li>
              {withBoldValues(t, "clients.prv.results.summaryBox.targetsBelow", { count: below })}
            </li>
            <li>
              {withBoldValues(t, "clients.prv.results.summaryBox.targetsOverflow", {
                count: overflow,
              })}
            </li>
            <li>
              {withBoldValues(t, "clients.prv.results.summaryBox.targetsNone", { count: none })}
            </li>
          </ul>
        </>
      );
    }

    return lines;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, scoped, selection, client, t]);

  // The platform's accent card (as the Inventory Scan status cards): left bar and
  // a 10 % tint in the client's brand colour, violet fallback for a TMA admin.
  // Prose with the key values in bold, no alert colours, no icons.
  return (
    <div
      className="flex flex-col gap-1.5 rounded-lg border border-l-4 px-4 py-3"
      style={{
        borderColor: "color-mix(in srgb, var(--assistant-accent) 24%, white)",
        borderLeftColor: "var(--assistant-accent)",
        backgroundColor: "color-mix(in srgb, var(--assistant-accent) 8%, white)",
      }}
    >
      <span className="text-[11px] font-semibold tracking-wider text-gray-500 uppercase">
        {t("clients.prv.results.summaryBox.title")}
      </span>
      <ul className="list-disc space-y-1 pl-4 text-sm text-gray-700">
        {bullets.map((line, index) => (
          <li key={index}>{line}</li>
        ))}
      </ul>
    </div>
  );
};

export default PrvSummaryBox;
